import {
  configToPrintOptions,
  defaultShipFromId,
  senderForId,
  SHIP_FROM_DEFAULTS,
} from './shipFromLocationsUtils';

const SHIP_FROM_STORAGE_KEY = 'saloni_admin_label_ship_from';

/** Labels per A4 sheet: 2 columns × 3 rows */
export const LABELS_PER_A4_PAGE = 6;
export const LABELS_A4_COLS = 2;
export const LABELS_A4_ROWS = 3;

/** @deprecated use API print_options — kept for fallback */
export const LABEL_SHIP_FROM_OPTIONS = configToPrintOptions(SHIP_FROM_DEFAULTS);

export function getSavedShipFromId(printOptions) {
  try {
    const id = localStorage.getItem(SHIP_FROM_STORAGE_KEY);
    if (id && (printOptions || []).some((o) => o.id === id)) return id;
  } catch {
    /* private mode */
  }
  return defaultShipFromId(null, printOptions);
}

export function saveShipFromId(id) {
  try {
    localStorage.setItem(SHIP_FROM_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function getSenderForShipFrom(id, printOptions) {
  return senderForId(printOptions || LABEL_SHIP_FROM_OPTIONS, id);
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Inner label markup (From / Deliver to / order #).
 * @param {object} order
 * @param {object} sender
 * @param {{ compact?: boolean }} [opts]
 */
export function buildLabelInnerHtml(order, sender, opts = {}) {
  const compact = Boolean(opts.compact);
  const shipping = order?.shipping || {};
  const itemCount = (order.items || []).reduce((n, it) => n + (Number(it.quantity) || 0), 0);
  const cityLine = [shipping.city, shipping.state].filter(Boolean).join(', ');
  const senderLines = (sender?.lines || []).filter(Boolean);

  return `
    <div class="label">
      <div class="section">
        <div class="eyebrow">From</div>
        <div class="from-name">${escapeHtml(sender?.name || 'Saloni Designs')}</div>
        ${senderLines.map((l) => `<div class="from-line">${escapeHtml(l)}</div>`).join('')}
        ${sender?.phone ? `<div class="from-line">Ph ${escapeHtml(sender.phone)}</div>` : ''}
      </div>
      <div class="section">
        <div class="eyebrow">Deliver to</div>
        <div class="to-name">${escapeHtml(shipping.name)}</div>
        ${shipping.phone ? `<div class="to-line">${escapeHtml(shipping.phone)}</div>` : ''}
        ${shipping.address ? `<div class="to-line">${escapeHtml(shipping.address)}</div>` : ''}
        ${cityLine ? `<div class="to-line">${escapeHtml(cityLine)}</div>` : ''}
        ${shipping.pincode ? `<div class="pin">PIN ${escapeHtml(shipping.pincode)}</div>` : ''}
      </div>
      <div class="meta">
        <div class="order-code">#${escapeHtml(order.order_number)}</div>
        ${itemCount > 0 ? `<div>${itemCount} item${itemCount === 1 ? '' : 's'}</div>` : ''}
      </div>
    </div>`;
}

function labelPageCss(labelSize, embeddedPreview = false) {
  const thermal = labelSize === 'thermal_4x6';
  const embed = embeddedPreview
    ? `
    body.label-preview-embed {
      padding: 8px;
      overflow: visible;
    }
    body.label-preview-embed .label {
      transform: scale(0.72);
      transform-origin: top left;
    }
    `
    : '';
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Segoe UI", system-ui, sans-serif;
      color: #111;
      background: #fff;
      padding: ${thermal ? '4mm' : '8mm'};
    }
    .label {
      width: ${thermal ? '100mm' : '100mm'};
      max-width: 100%;
      border: 2px solid #111;
      padding: ${thermal ? '4mm' : '6mm'};
    }
    .section { margin-bottom: 4mm; }
    .section:last-child { margin-bottom: 0; }
    .eyebrow {
      font-size: 8pt;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #444;
      margin-bottom: 1.5mm;
    }
    .from-name { font-size: 10pt; font-weight: 600; }
    .from-line { font-size: 8.5pt; line-height: 1.35; color: #333; }
    .to-name {
      font-size: ${thermal ? '14pt' : '16pt'};
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 2mm;
    }
    .to-line { font-size: ${thermal ? '10pt' : '11pt'}; line-height: 1.45; }
    .pin {
      font-size: ${thermal ? '16pt' : '18pt'};
      font-weight: 800;
      letter-spacing: 0.04em;
      margin-top: 2mm;
    }
    .meta {
      margin-top: 4mm;
      padding-top: 2mm;
      border-top: 1px dashed #999;
      font-size: 8pt;
      color: #333;
    }
    .meta strong { font-size: 9pt; color: #111; }
    .order-code {
      font-size: 11pt;
      font-weight: 700;
      letter-spacing: 0.05em;
      margin-top: 1mm;
    }
    @media print {
      body { padding: 0; }
      @page {
        margin: ${thermal ? '3mm' : '8mm'};
        size: ${thermal ? '100mm 150mm' : 'auto'};
      }
    }
    ${embed}
  `;
}

function a4SheetPageCss(compact) {
  const toName = compact ? '12pt' : '13pt';
  const toLine = compact ? '8.5pt' : '9pt';
  const pin = compact ? '13pt' : '14pt';
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Segoe UI", system-ui, sans-serif;
      color: #111;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .print-hint {
      font-size: 9pt;
      color: #555;
      padding: 6mm 8mm 0;
    }
    .a4-sheet {
      display: grid;
      grid-template-columns: repeat(${LABELS_A4_COLS}, 1fr);
      grid-template-rows: repeat(${LABELS_A4_ROWS}, 1fr);
      gap: 3mm;
      width: 194mm;
      height: 281mm;
      margin: 0 auto;
      padding: 0;
      page-break-after: always;
      break-after: page;
    }
    .a4-sheet:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .label-cell {
      min-height: 0;
      overflow: hidden;
      display: flex;
    }
    .label-cell--empty {
      border: 1px dashed #ddd;
      border-radius: 2px;
    }
    .label-cell .label {
      width: 100%;
      height: 100%;
      border: 2px solid #111;
      padding: 3mm;
      display: flex;
      flex-direction: column;
    }
    .label-cell .section { margin-bottom: 2mm; flex-shrink: 0; }
    .label-cell .section:last-of-type { flex: 1; min-height: 0; }
    .label-cell .eyebrow { font-size: 6.5pt; margin-bottom: 1mm; }
    .label-cell .from-name { font-size: 8pt; }
    .label-cell .from-line { font-size: 7pt; line-height: 1.3; }
    .label-cell .to-name { font-size: ${toName}; margin-bottom: 1mm; }
    .label-cell .to-line { font-size: ${toLine}; line-height: 1.35; }
    .label-cell .pin { font-size: ${pin}; margin-top: 1mm; }
    .label-cell .meta { margin-top: auto; padding-top: 1.5mm; font-size: 7pt; }
    .label-cell .order-code { font-size: 9pt; }
    @media print {
      .print-hint { display: none !important; }
      body { margin: 0; }
      @page { size: A4 portrait; margin: 8mm; }
      .a4-sheet {
        width: 100%;
        height: 277mm;
        max-height: 277mm;
      }
    }
  `;
}

/**
 * @param {object} order
 * @param {object} sender
 * @param {'standard'|'thermal_4x6'} [labelSize]
 * @param {boolean} [embeddedPreview] scale down for admin iframe preview
 */
export function buildLabelPreviewHtml(order, sender, labelSize = 'thermal_4x6', embeddedPreview = false) {
  const size = labelSize === 'standard' ? 'standard' : 'thermal_4x6';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Label ${escapeHtml(order.order_number)}</title>
  <style>${labelPageCss(size, embeddedPreview)}</style>
</head>
<body class="${embeddedPreview ? 'label-preview-embed' : ''}">
  ${buildLabelInnerHtml(order, sender, { compact: false })}
</body>
</html>`;
}

/**
 * Multiple shipping labels tiled on A4 (6 per page).
 * @param {object[]} orders — full order objects with shipping
 * @param {object|function(object): object} sender — fixed sender or resolver per order
 */
export function buildShippingLabelsA4SheetHtml(orders, sender) {
  const resolveSender =
    typeof sender === 'function' ? sender : () => sender;

  const valid = (orders || []).filter((o) => {
    const s = o?.shipping;
    return s && (s.name || s.address);
  });

  if (!valid.length) {
    return null;
  }

  const pages = [];
  for (let i = 0; i < valid.length; i += LABELS_PER_A4_PAGE) {
    pages.push(valid.slice(i, i + LABELS_PER_A4_PAGE));
  }

  const sheetsHtml = pages
    .map((pageOrders) => {
      const cells = pageOrders
        .map(
          (o) =>
            `<div class="label-cell">${buildLabelInnerHtml(o, resolveSender(o), { compact: true })}</div>`
        )
        .join('');
      const pad = LABELS_PER_A4_PAGE - pageOrders.length;
      const empty =
        pad > 0
          ? Array(pad)
              .fill('<div class="label-cell label-cell--empty" aria-hidden="true"></div>')
              .join('')
          : '';
      return `<div class="a4-sheet">${cells}${empty}</div>`;
    })
    .join('');

  const pageCount = pages.length;
  const labelCount = valid.length;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Shipping labels (${labelCount})</title>
  <style>${a4SheetPageCss(true)}</style>
</head>
<body>
  <p class="print-hint">Shipping labels: ${labelCount} label${labelCount === 1 ? '' : 's'} · ${pageCount} A4 page${pageCount === 1 ? '' : 's'} (${LABELS_PER_A4_PAGE} per page). Cut along borders. In print dialog choose A4.</p>
  ${sheetsHtml}
</body>
</html>`;
}

function printViaHiddenIframe(html, checkSelector = '.label') {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Shipping label');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:0;top:0;width:210mm;height:297mm;border:0;z-index:-1;opacity:0;pointer-events:none;';

    let settled = false;
    let failTimer = 0;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(failTimer);
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
      resolve(ok);
    };

    iframe.onload = () => {
      window.setTimeout(() => {
        try {
          const win = iframe.contentWindow;
          if (!win?.document?.querySelector(checkSelector)) {
            finish(false);
            return;
          }
          win.focus();
          win.print();
          finish(true);
        } catch {
          finish(false);
        }
      }, 150);
    };

    iframe.onerror = () => finish(false);
    failTimer = window.setTimeout(() => finish(false), 8000);

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

function printViaBlobWindow(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) {
    URL.revokeObjectURL(url);
    return false;
  }
  const revoke = () => URL.revokeObjectURL(url);
  win.addEventListener('load', revoke, { once: true });
  window.setTimeout(revoke, 120000);
  window.setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* user can Ctrl+P */
    }
  }, 600);
  return true;
}

/**
 * Print up to many orders on A4 — 6 labels per sheet.
 * @param {object[]} orders
 * @param {object|function(object): object} sender
 */
export async function printShippingLabelsA4Sheet(orders, sender) {
  const html = buildShippingLabelsA4SheetHtml(orders, sender);
  if (!html) {
    window.alert('No orders with a delivery address to print.');
    return false;
  }

  const iframeOk = await printViaHiddenIframe(html, '.a4-sheet');
  if (iframeOk) return true;

  if (printViaBlobWindow(html)) return true;

  window.alert('Could not open the print dialog. Allow pop-ups for this site and try again.');
  return false;
}

/**
 * Print one shipping label.
 * Thermal: one label per roll. Standard A4: one label in top-left of a 6-up sheet (no full-page waste).
 *
 * @param {object} order
 * @param {object} [sender]
 * @param {'standard'|'thermal_4x6'} [labelSize]
 */
export async function printShippingLabel(order, sender, labelSize = 'thermal_4x6') {
  const shipping = order?.shipping;
  if (!shipping?.name && !shipping?.address) {
    window.alert('No shipping address on this order.');
    return;
  }

  if (labelSize === 'standard') {
    await printShippingLabelsA4Sheet([order], sender);
    return;
  }

  const html = buildLabelPreviewHtml(order, sender, labelSize);
  const iframeOk = await printViaHiddenIframe(html);
  if (iframeOk) return;

  if (printViaBlobWindow(html)) return;

  window.alert('Could not open the print dialog. Allow pop-ups for this site and try again.');
}